import {
  CheckCircleTwoTone,
  ControlTwoTone,
  DeleteOutlined,
  IdcardTwoTone,
  MoneyCollectTwoTone,
  PauseCircleTwoTone,
  ScheduleTwoTone,
  TagsTwoTone,
} from '@ant-design/icons';
import { SchedulerProvider, SchedulersList } from '@teamco/ischeduler-antd';
import {
  Col,
  Collapse,
  type CollapseProps,
  Form,
  type FormProps,
  Row,
} from 'antd';
import { t } from 'i18next';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { deleteWarning } from '@client/components/actions/modal.delete';
import { CloseButton, SaveButton } from '@client/components/buttons';
import styles from '@client/components/components.module.less';
import { EditPageTitle } from '@client/components/EditPageTitle';
import { formMainInfo } from '@client/components/formMainInfo';
import { QuestionTooltip } from '@client/components/QuestionTooltip';
import { StatusTag } from '@client/components/StatusTag';
import { TagsComponent } from '@client/components/tags/TagsComponent';
import { Toolbar } from '@client/components/Toolbar';
import { NotificationContext } from '@client/context/NotificationContext';
import { EAbilityActions, EAbilitySubjects } from '@client/hooks/useAbilities';
import { useAuth } from '@client/hooks/useAuth';
import { useFormProps } from '@client/hooks/useFormProps';
import { useOnFinish } from '@client/hooks/useOnFinish';
import { usePrompt } from '@client/hooks/usePrompt';
import {
  useAssignedSchedulers,
  useSchedulers,
} from '@client/hooks/useSchedulers';
import { useSubscription } from '@client/hooks/useSubscription';
import { PageLayout } from '@client/layouts/page.layout';
import { SubscriptionDiscount } from '@client/pages/subscriptions/subscription/subscription.discount';
import { SubscriptionInfo } from '@client/pages/subscriptions/subscription/subscription.info';
import {
  areSameScheduler,
  createSchedulers,
  schedulerDatesToDayjs,
  syncSchedulers,
} from '@client/services/scheduler.service';
import { useAssigned } from '@client/store/assigned.store';
import { useDirty } from '@client/store/dirty.store';
import { useLoading } from '@client/store/loading.store';
import { CNsTags } from '@client/types/form.type';
import { ERoutePaths } from '@client/types/router.type';
import { isEditMode } from '@client/utils/form.util';
import { flexLayout } from '@client/utils/layout.util';
import {
  isDisabledScheduler,
  onCreateScheduler as handleCreate,
  onUpdateScheduler as handleUpdate,
  onDeleteScheduler as handleDelete,
} from '@client/utils/scheduler.util';
import {
  type IFeature,
  type IScheduler,
  CNsPrice,
  ECurrency,
  DEFAULT_SUBSCRIPTION,
  ESubscriptionType,
  type ISubscription,
  EDurationTypes,
  EStatus,
  type IMetadata,
  CScheduler,
  ESchedulerPrefix,
  CSchedulerEntityPaths,
} from '@common/types';

import { FeaturesDrawerButton } from './panels/FeaturesDrawerButton';
import { SubscriptionFeatures } from './subscription.features';

import type { ItemType } from 'antd/es/menu/interface';

export const SubscriptionEditPage: React.FC = () => {
  const [formRef] = Form.useForm();
  const { ability } = useAuth();
  const { isDirty, setDirty } = useDirty();
  const { setLoading } = useLoading();
  const { modalApi } = useContext(NotificationContext);

  const [activeKeys, setActiveKeys] = useState<string[]>(['info']);

  const params = useParams();
  const navigate = useNavigate();

  const isEdit = isEditMode(params.subscriptionId);

  const { assignedSchedulers, setAssignedSchedulers } = useAssignedSchedulers({
    [ESchedulerPrefix.SALE]: [],
    [ESchedulerPrefix.DISCOUNT]: [],
  });

  const { assigned: assignedFeatures, setAssigned } = useAssigned<IFeature>();

  const {
    loading,
    subscription,
    onDelete,
    onCreate,
    onUpdate,
    onGetFeatures,
    onActivate,
    refetch,
  } = useSubscription();

  const {
    loading: saleLoading,
    refetch: saleOnRefresh,
    schedulers: saleSchedulers,
    onCreate: onCreateScheduler,
    onUpdate: onUpdateScheduler,
    onDelete: onDeleteScheduler,
  } = useSchedulers<IScheduler>(
    params.subscriptionId as string,
    CSchedulerEntityPaths.SUBSCRIPTIONS,
    ESchedulerPrefix.SALE,
  );

  const {
    loading: discountLoading,
    refetch: discountOnRefresh,
    schedulers: discountSchedulers,
  } = useSchedulers<IScheduler>(
    params.subscriptionId as string,
    CSchedulerEntityPaths.SUBSCRIPTIONS,
    ESchedulerPrefix.DISCOUNT,
  );

  useEffect(() => {
    if (isDirty) return;

    setAssignedSchedulers((prev) => {
      const prevMap = prev[CScheduler];
      const nextDiscount = discountSchedulers || [];
      const nextSale = saleSchedulers || [];

      const noChange =
        areSameScheduler(prevMap[ESchedulerPrefix.DISCOUNT], nextDiscount) &&
        areSameScheduler(prevMap[ESchedulerPrefix.SALE], nextSale);

      if (noChange) return prev;

      return {
        [CScheduler]: {
          ...prevMap,
          [ESchedulerPrefix.DISCOUNT]: nextDiscount,
          [ESchedulerPrefix.SALE]: nextSale,
        },
      };
    });
  }, [discountSchedulers, isDirty, saleSchedulers, setAssignedSchedulers]);

  useEffect(() => {
    if (subscription) {
      setLoading(true); // Set loading during form initialization
      formRef.setFieldsValue(subscription);

      // Use setTimeout to ensure setFieldsValue completes first
      setTimeout(() => {
        setDirty(false); // Reset any false dirty state
        setLoading(false); // Mark initialization complete
      }, 0);
    }
  }, [subscription, formRef, setLoading, setDirty]);

  useEffect(() => {
    if (isEdit && subscription) {
      fetchFeatures();
    } else if (!isEdit) {
      setAssigned([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, subscription, setAssigned]);

  const fetchFeatures = useCallback(() => {
    onGetFeatures().then((features: IFeature[]) => {
      setAssigned(features || []);
    });
  }, [onGetFeatures, setAssigned]);

  const handleChange = (keys: string | string[]) => {
    setActiveKeys(Array.isArray(keys) ? keys : [keys]);
  };

  const subject = EAbilitySubjects.SUBSCRIPTION;

  const disabled = loading || ability.cannot(EAbilityActions.MANAGE, subject);

  const entity = t('subscription');

  const { onSuccess } = useOnFinish({ formRef, isEdit, entity });

  usePrompt();

  const handleRefresh = async () => {
    await refetch();
    formRef.setFieldsValue(subscription);
    setDirty(false);
  };

  const { DEFAULT_FORM_PROPS } = useFormProps(loading, formRef);

  const formProps = {
    ...DEFAULT_FORM_PROPS,
    async onFinish(formValues: ISubscription) {
      if (isEdit) {
        await onUpdate(params.subscriptionId, formValues);
        await syncSchedulers({
          entityId: params.subscriptionId,
          schedulers: assignedSchedulers,
          onCreate: onCreateScheduler,
          onUpdate: onUpdateScheduler,
        });
        await onSuccess<ISubscription>(params.featureId);
      } else {
        const subscription = await onCreate(formValues);
        if (subscription?.id) {
          await onSuccess<ISubscription>(
            `${ERoutePaths.SUBSCRIPTIONS}/${subscription.id}`,
            true,
          );
          await createSchedulers({
            entityId: subscription.id,
            schedulers: assignedSchedulers,
            onCreate: onCreateScheduler,
          });
        }
      }
    },
    initialValues: DEFAULT_SUBSCRIPTION,
  } as FormProps<ISubscription>;

  const discountProps = {
    disabled,
    formRef,
    loading,
    currencies: Object.keys(ECurrency) as (keyof typeof ECurrency)[],
    durationTypes: Object.keys(
      EDurationTypes,
    ) as (keyof typeof EDurationTypes)[],
  };

  const tagsProps = {
    formRef,
    disabled,
    ableFor: {
      ability,
      subject: EAbilitySubjects.TAGS,
    },
    onUpdateTags: (tags: string[]) => {
      formRef.setFieldsValue({ [CNsTags]: tags });
    },
    tags: subscription?.tags as string[],
    loading,
  };

  const schedulerSubject = `${subject}.${EAbilitySubjects.SCHEDULER}`;

  const schedulerProviderProps = {
    schedulers: Object.fromEntries(
      Object.entries(assignedSchedulers[CScheduler]).map(([key, list]) => [
        key,
        (list as IScheduler[]).map(schedulerDatesToDayjs),
      ]),
    ) as any,
    onCreate: async (type: ESchedulerPrefix, scheduler: any) =>
      handleCreate(
        type,
        scheduler as IScheduler,
        setAssignedSchedulers,
        setDirty,
      ),
    onUpdate: async (type: ESchedulerPrefix, scheduler: any) =>
      handleUpdate(
        type,
        scheduler as IScheduler,
        setAssignedSchedulers,
        setDirty,
      ),
    onDelete: async (type: ESchedulerPrefix, schedulerId: string) =>
      handleDelete(
        type,
        schedulerId,
        assignedSchedulers,
        onDeleteScheduler,
        setDirty,
      ),
    loading: loading || saleLoading || discountLoading,
    disabled: isDisabledScheduler(subject, disabled, isEdit, ability),
    permissions: {
      canCreate: ability.can(EAbilityActions.CREATE, schedulerSubject),
      canUpdate: ability.can(EAbilityActions.UPDATE, schedulerSubject),
      canDelete: ability.can(EAbilityActions.DELETE, schedulerSubject),
    },
  };

  const subscriptionItems: CollapseProps['items'] = [
    {
      key: 'info',
      forceRender: true,
      label: (
        <div className={styles.chipLabel}>
          <IdcardTwoTone />
          {t('subscription.info')}
        </div>
      ),
      children: (
        <SubscriptionInfo
          disabled={disabled}
          subscriptionTypes={
            Object.keys(ESubscriptionType) as (keyof typeof ESubscriptionType)[]
          }
        />
      ),
      extra: <QuestionTooltip title={t('subscription.info.helper')} />,
    },
    {
      key: CNsPrice,
      label: (
        <div className={styles.chipLabel}>
          <MoneyCollectTwoTone />
          {t('price.priceInfo', { type: t('subscription') })}
        </div>
      ),
      forceRender: true,
      children: (
        <Row gutter={[24, 24]}>
          <Col {...flexLayout.fullColumn}>
            <SubscriptionDiscount
              {...discountProps}
              renderScheduler={(readOnlyFields): React.ReactNode => (
                <SchedulerProvider {...schedulerProviderProps}>
                  <SchedulersList
                    onRefresh={() => !isDirty && discountOnRefresh()}
                    readOnlyFields={readOnlyFields}
                    title={t('price.discount')}
                    type={ESchedulerPrefix.DISCOUNT}
                  />
                </SchedulerProvider>
              )}
            />
          </Col>
        </Row>
      ),
      extra: <QuestionTooltip title={t('price.info.helper')} />,
    },
    {
      key: 'scheduler',
      label: (
        <div className={styles.chipLabel}>
          <ScheduleTwoTone />
          {t('scheduler.header.title', {
            entity: t('price.saleAt'),
            count: assignedSchedulers[CScheduler][ESchedulerPrefix.SALE].length,
          })}
        </div>
      ),
      children: (
        <Row gutter={[24, 24]}>
          <Col {...flexLayout.fullColumn}>
            <SchedulerProvider {...schedulerProviderProps}>
              <SchedulersList
                onRefresh={() => !isDirty && saleOnRefresh()}
                title={t('price.saleAt')}
                type={ESchedulerPrefix.SALE}
              />
            </SchedulerProvider>
          </Col>
        </Row>
      ),
      extra: <QuestionTooltip title={t('scheduler.info.helper')} />,
    },
    {
      key: 'features',
      label: (
        <div className={styles.chipLabel}>
          <ControlTwoTone />
          {t('subscription.assigned.features', {
            count: assignedFeatures.length,
          })}
        </div>
      ),
      children: (
        <Row gutter={[24, 24]}>
          <Col {...flexLayout.fullColumn}>
            <SubscriptionFeatures disabled={disabled} />
          </Col>
        </Row>
      ),
      extra: (
        <div className={styles.extraPanel}>
          <FeaturesDrawerButton
            disabled={disabled || !activeKeys.includes('features')}
          />
          <QuestionTooltip title={t('subscription.features.helper')} />
        </div>
      ),
    },
    {
      key: CNsTags,
      forceRender: true,
      label: (
        <div className={styles.chipLabel}>
          <TagsTwoTone />
          {t('form.tags')}
        </div>
      ),
      children: (
        <Form.Item noStyle name={[CNsTags]}>
          <TagsComponent {...tagsProps} />
        </Form.Item>
      ),
      extra: <QuestionTooltip title={t('form.tag.helper')} />,
    },
    {
      ...formMainInfo({
        metadata: subscription?.metadata as IMetadata,
        disabled: disabled || !isEdit,
        navigate,
      }),
    },
  ];

  return (
    <PageLayout
      title={<EditPageTitle isEdit={isEdit} name={entity} />}
      action={EAbilityActions.MANAGE}
      extra={[
        <div key={'status'} className={styles.extraPanel}>
          <StatusTag status={subscription?.status} />
        </div>,
        <CloseButton
          key="close"
          loading={loading}
          disabled={disabled}
          onClick={() => navigate(ERoutePaths.SUBSCRIPTIONS)}
        />,
        <SaveButton
          key="save"
          formRef={formRef}
          disabled={disabled || (!disabled && !isDirty)}
          loading={loading}
          isEdit={isEdit}
          ableFor={{ ability, subject }}
        />,
        <Toolbar<ISubscription[]>
          key="toolbar"
          onRefresh={handleRefresh}
          size="small"
          entities={[subscription] as ISubscription[]}
          subject={subject}
          items={
            [
              {
                label:
                  subscription?.status === EStatus.ACTIVE
                    ? t('actions.deactivate')
                    : t('actions.activate'),
                icon:
                  subscription?.status === EStatus.ACTIVE ? (
                    <PauseCircleTwoTone />
                  ) : (
                    <CheckCircleTwoTone />
                  ),
                onClick: async () => {
                  await onActivate?.({
                    ...subscription,
                    id: params.subscriptionId,
                  });
                  await refetch();
                },
                disabled: disabled || !isEdit || isDirty,
              },
              {
                label: t('actions.deleteEntity', { type: t('subscription') }),
                icon: <DeleteOutlined />,
                onClick: () =>
                  deleteWarning({
                    status: subscription?.status,
                    translateKey: t('subscription') as string,
                    onApprove: () =>
                      onDelete?.({
                        ...subscription,
                        id: params.subscriptionId,
                      }),
                    config: { modalApi },
                  }),
                danger: true,
                disabled:
                  disabled ||
                  subscription?.status !== EStatus.DEACTIVATED ||
                  !isEdit,
              },
            ] as unknown as ItemType[]
          }
        />,
      ]}
      subject={subject}
    >
      <Form {...formProps}>
        <Collapse
          items={subscriptionItems}
          activeKey={activeKeys}
          onChange={handleChange}
        />
      </Form>
    </PageLayout>
  );
};
